import { CheckCircle, LocationOn } from '@mui/icons-material';
import { Box, CircularProgress, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  BuildChecklistEntity,
  ChecklistItemEntity,
  PreferenceCardEntity,
} from '@peritasai/repository';
import { Typography as UITypography } from '@peritasai/ui-components';
import { themeColors as periverseColors } from '@peritasai/ui-shared';
import React, { useMemo } from 'react';

const ListsContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr auto 1fr',
  gap: theme.spacing(3),
  marginTop: theme.spacing(3),
  flex: 1,
  minHeight: 0,
}));

const VerticalDivider = styled(Box)(({ theme }) => ({
  width: '1px',
  backgroundColor: theme.palette.divider,
  height: '100%',
  // Start below the column headers
}));

const ListColumn = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  minHeight: 0,
}));

const ListHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingBottom: theme.spacing(1),
  // borderBottom: `1px solid ${theme.palette.divider}`,
}));

const ListTitle = styled(UITypography)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightMedium,
  color: theme.palette.text.primary,
}));

const ListContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
}));

const OutstandingItemCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'disabled',
})<{ disabled?: boolean }>(({ theme, disabled }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  cursor: disabled ? 'default' : 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': disabled
    ? {}
    : {
        borderColor: periverseColors.peritaspurple[500],
        backgroundColor: theme.palette.action.hover,
        boxShadow: `0 2px 4px rgba(0, 0, 0, 0.1)`,
      },
}));

const PickedItemCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${periverseColors.peritasblue[200]}`,
  backgroundColor: '#E8F5E9', // Light green background
  position: 'relative',
}));

const ItemName = styled(UITypography)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightMedium,
  marginBottom: theme.spacing(0.5),
  color: theme.palette.text.primary,
}));

const ItemDetails = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
}));

const ItemDetailRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  fontSize: theme.typography.caption.fontSize,
  color: theme.palette.text.secondary,
}));

const LocationPin = styled(LocationOn)(({ theme }) => ({
  fontSize: 16,
  color: periverseColors.peritasblue[600],
}));

const StatusBadge = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  marginTop: theme.spacing(1),
  fontSize: theme.typography.caption.fontSize,
  color: periverseColors.peritaspurple[500],
}));

const CheckmarkIcon = styled(CheckCircle)({
  fontSize: 16,
  color: '#4CAF50',
});

const LoadingOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  zIndex: 2,
  borderRadius: theme.shape.borderRadius,
}));

const Overlay = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(255, 255, 255, 0.5)',
  pointerEvents: 'none', // Allow scrolling but prevent clicks
  zIndex: 1,
});

const EmptyState = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  textAlign: 'center',
  color: theme.palette.text.secondary,
}));

interface ItemDetectionStatusComponentProps {
  phase: 'cart' | 'tray';
  preferenceCard: PreferenceCardEntity;
  buildChecklist: BuildChecklistEntity;
  onItemClick: (checklistItem: ChecklistItemEntity) => Promise<void>;
  isLoading?: boolean;
}

export const ItemDetectionStatusComponent: React.FC<
  ItemDetectionStatusComponentProps
> = ({
  phase,
  preferenceCard,
  buildChecklist,
  onItemClick,
  isLoading = false,
}) => {
  // Always show trays regardless of phase
  const preferenceItems = useMemo(() => {
    return preferenceCard.trackedAssets.trays;
  }, [preferenceCard]);

  // Always use tray checklist groups regardless of phase
  const checklistGroups = useMemo(() => {
    return buildChecklist.getTrays();
  }, [buildChecklist]);

  // Create a map of catalogItemId to checklist group for quick lookup
  const checklistGroupByCatalogId = useMemo(() => {
    const map = new Map<string, (typeof checklistGroups)[0]>();
    checklistGroups.forEach((group) => {
      map.set(group.catalogItemId, group);
    });
    return map;
  }, [checklistGroups]);

  // Get outstanding items (preference items that haven't been fully allocated)
  const outstandingItems = useMemo(() => {
    return preferenceItems
      .map((prefItem) => {
        const checklistGroup = checklistGroupByCatalogId.get(
          prefItem.catalogItemId
        );
        const allocatedCount = checklistGroup?.allocatedCount ?? 0;
        const requiredCount = prefItem.quantity ?? 0;
        const remainingCount = requiredCount - allocatedCount;

        if (remainingCount <= 0) {
          return null; // Fully allocated, don't show in outstanding
        }

        // Find the first unallocated checklist item for this preference item
        const unallocatedItem = checklistGroup?.items.find(
          (item) => !item.allocated
        );

        // Check if item has availability
        const hasAvailability = (prefItem.availableCount ?? 0) > 0;

        return {
          preferenceItem: prefItem,
          checklistItem: unallocatedItem,
          remainingCount,
          hasAvailability,
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);
  }, [preferenceItems, checklistGroupByCatalogId]);

  // Get picked items (allocated checklist items)
  const pickedItems = useMemo(() => {
    const items: Array<{
      checklistItem: ChecklistItemEntity;
      preferenceItem: (typeof preferenceItems)[0];
    }> = [];

    checklistGroups.forEach((group) => {
      const preferenceItem = preferenceItems.find(
        (item) => item.catalogItemId === group.catalogItemId
      );

      // Only process items that have a matching preference item
      if (preferenceItem) {
        group.items.forEach((checklistItem) => {
          if (checklistItem.allocated) {
            items.push({
              checklistItem,
              preferenceItem,
            });
          }
        });
      }
    });

    return items;
  }, [checklistGroups, preferenceItems]);

  const handleItemClick = async (
    checklistItem: ChecklistItemEntity | undefined,
    hasAvailability: boolean
  ) => {
    // Disable clicks during cart phase, when loading, or when there's no availability
    if (phase === 'cart' || isLoading || !checklistItem || !hasAvailability)
      return;
    await onItemClick(checklistItem);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      {isLoading && (
        <LoadingOverlay>
          <CircularProgress size={40} />
        </LoadingOverlay>
      )}

      {phase === 'cart' && <Overlay />}

      <ListsContainer>
        {/* Outstanding Column */}
        <ListColumn>
          <ListHeader>
            <ListTitle variant="h6">
              {outstandingItems.length} Outstanding
            </ListTitle>
          </ListHeader>
          <ListContent>
            {outstandingItems.length === 0 ? (
              <EmptyState>
                <Typography variant="body2" color="text.secondary">
                  No outstanding items
                </Typography>
              </EmptyState>
            ) : (
              outstandingItems.map((item, index) => {
                const prefItem = item.preferenceItem;
                const location =
                  prefItem.availableLocations &&
                  prefItem.availableLocations.length > 0
                    ? prefItem.availableLocations[0]
                    : null;

                const isDisabled = phase === 'cart' || !item.hasAvailability;

                return (
                  <OutstandingItemCard
                    key={`${prefItem.catalogItemId}-${index}`}
                    onClick={() =>
                      handleItemClick(item.checklistItem, item.hasAvailability)
                    }
                    disabled={isDisabled}
                  >
                    <ItemName variant="body2">{prefItem.name}</ItemName>
                    <ItemDetails>
                      <ItemDetailRow>
                        <Typography variant="caption" color="text.secondary">
                          Instrument Type ID: {prefItem.productNumber}
                        </Typography>
                      </ItemDetailRow>
                      <ItemDetailRow>
                        <Typography variant="caption" color="text.secondary">
                          Quantity: {prefItem.quantity}
                        </Typography>
                      </ItemDetailRow>
                      {location && (
                        <ItemDetailRow>
                          <LocationPin />
                          <Typography variant="caption" color="text.secondary">
                            {location.name} - {prefItem.availableCount}{' '}
                            Available
                          </Typography>
                        </ItemDetailRow>
                      )}
                    </ItemDetails>
                  </OutstandingItemCard>
                );
              })
            )}
          </ListContent>
        </ListColumn>

        {/* Vertical Divider */}
        <VerticalDivider />

        {/* Picked Column */}
        <ListColumn>
          <ListHeader>
            <ListTitle variant="h6">{pickedItems.length} Picked</ListTitle>
          </ListHeader>
          <ListContent>
            {pickedItems.length === 0 ? (
              <EmptyState>
                <Typography variant="body2" color="text.secondary">
                  No items picked yet
                </Typography>
              </EmptyState>
            ) : (
              pickedItems.map((item, index) => {
                const prefItem = item.preferenceItem;
                const checklistItem = item.checklistItem;

                // Safety check: skip rendering if preferenceItem is missing
                if (!prefItem) {
                  return null;
                }

                return (
                  <PickedItemCard
                    key={`${checklistItem.checklistItemId}-${index}`}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <ItemName variant="body2">{prefItem.name}</ItemName>
                        <ItemDetails>
                          <ItemDetailRow>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Instrument Type ID: {prefItem.productNumber}
                            </Typography>
                          </ItemDetailRow>
                          {checklistItem.assetId && (
                            <StatusBadge>
                              <CheckmarkIcon />
                              <Typography variant="caption">
                                Tray {checklistItem.assetId} Detected
                              </Typography>
                            </StatusBadge>
                          )}
                        </ItemDetails>
                      </Box>
                    </Box>
                  </PickedItemCard>
                );
              })
            )}
          </ListContent>
        </ListColumn>
      </ListsContainer>
    </Box>
  );
};
